if (!customElements.get('blog-archive')) {
  customElements.define(
    'blog-archive',
    class BlogArchive extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
        this.perPageDesktop = parseInt(this.dataset.perPage, 10) || 12;
        this.perPageMobile = parseInt(this.dataset.perPageMobile, 10) || 6;
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.perPage = this.mobileQuery.matches ? this.perPageMobile : this.perPageDesktop;
        this.showTitle = this.dataset.showTitle === 'true';
        this.showAuthor = this.dataset.showAuthor === 'true';
        this.showDate = this.dataset.showDate === 'true';
        this.showTags = this.dataset.showTags === 'true';
        this.accentScheme = this.dataset.accentScheme || 'scheme-1';
        this.productFilterFormat = this.dataset.productFilterFormat || 'handle';
        this.tagFilterAllLabel = this.dataset.tagFilterAllLabel || 'All posts';
        this.productFilterAllLabel = this.dataset.productFilterAllLabel || 'All products';

        this.gridEl = this.querySelector('[data-blog-archive-grid]');
        this.countEl = this.querySelector('[data-blog-archive-count]');
        this.paginationEl = this.querySelector('[data-blog-archive-pagination]');
        this.emptyEl = this.querySelector('[data-blog-archive-empty]');
        this.searchInput = this.querySelector('[data-blog-archive-search]');
        this.tagSelect = this.querySelector('[data-blog-archive-tag-filter]');
        this.productSelect = this.querySelector('[data-blog-archive-product-filter]');

        this.currentPage = 1;
        this.filters = { search: '', tag: '', product: '' };
        this.allArticles = this.readEmbeddedArticles(this);
        this.filtered = [...this.allArticles];

        this.populateTagFilter();
        this.populateProductFilter();
        this.bindEvents();
        // The server rendered page 1's cards using the desktop page size,
        // so only skip the redraw if that's also correct for this viewport.
        // On a mobile viewport, redraw immediately with the mobile count.
        this.renderCurrentPage({ skipGrid: !this.mobileQuery.matches });

        if (this.totalPages > 1) {
          this.fetchRemainingPages();
        }

        this.onBreakpointChange = (event) => {
          const nextPerPage = event.matches ? this.perPageMobile : this.perPageDesktop;
          if (nextPerPage === this.perPage) return;
          this.perPage = nextPerPage;
          this.currentPage = 1;
          this.renderCurrentPage();
        };
        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener('change', this.onBreakpointChange);
        } else if (this.mobileQuery.addListener) {
          this.mobileQuery.addListener(this.onBreakpointChange);
        }
      }

      disconnectedCallback() {
        if (!this.mobileQuery || !this.onBreakpointChange) return;
        if (this.mobileQuery.removeEventListener) {
          this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
        } else if (this.mobileQuery.removeListener) {
          this.mobileQuery.removeListener(this.onBreakpointChange);
        }
      }

      readEmbeddedArticles(root) {
        const script = root.querySelector('script[data-blog-archive-payload]');
        if (!script) return [];
        try {
          const data = JSON.parse(script.textContent);
          return Array.isArray(data.articles) ? data.articles : [];
        } catch (error) {
          return [];
        }
      }

      async fetchRemainingPages() {
        const requests = [];
        for (let page = 2; page <= this.totalPages; page++) {
          const pageUrl = new URL(window.location.href);
          pageUrl.searchParams.set('section_id', this.sectionId);
          pageUrl.searchParams.set('page', page);
          requests.push(
            fetch(pageUrl.toString())
              .then((response) => response.text())
              .then((html) => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const root = doc.querySelector(`[data-section-id="${this.sectionId}"]`);
                return root ? this.readEmbeddedArticles(root) : [];
              })
              .catch(() => [])
          );
        }

        const results = await Promise.all(requests);
        results.forEach((pageArticles) => {
          this.allArticles.push(...pageArticles);
        });
        this.populateTagFilter();
        this.populateProductFilter();
        this.applyFilters();
      }

      populateTagFilter() {
        if (!this.tagSelect) return;
        const currentValue = this.tagSelect.value;
        const tags = new Set();
        this.allArticles.forEach((article) => (article.tags || []).forEach((tag) => tags.add(tag)));

        this.tagSelect.innerHTML = `<option value="">${this.escapeHtml(this.tagFilterAllLabel)}</option>`;
        Array.from(tags)
          .sort((a, b) => a.localeCompare(b))
          .forEach((tag) => {
            const option = document.createElement('option');
            option.value = tag;
            option.textContent = tag;
            this.tagSelect.appendChild(option);
          });

        if (tags.has(currentValue)) this.tagSelect.value = currentValue;
      }

      populateProductFilter() {
        if (!this.productSelect) return;
        const currentValue = this.productSelect.value;
        const products = new Set();
        this.allArticles.forEach((article) => (article.products || []).forEach((product) => products.add(product)));

        this.productSelect.innerHTML = `<option value="">${this.escapeHtml(this.productFilterAllLabel)}</option>`;
        Array.from(products)
          .sort((a, b) => a.localeCompare(b))
          .forEach((product) => {
            const option = document.createElement('option');
            option.value = product;
            option.textContent =
              this.productFilterFormat === 'as_entered'
                ? product
                : product.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
            this.productSelect.appendChild(option);
          });

        if (products.has(currentValue)) this.productSelect.value = currentValue;
      }

      bindEvents() {
        if (this.searchInput) {
          let debounceTimer;
          this.searchInput.addEventListener('input', (event) => {
            clearTimeout(debounceTimer);
            const value = event.target.value;
            debounceTimer = setTimeout(() => {
              this.filters.search = value.trim().toLowerCase();
              this.currentPage = 1;
              this.applyFilters();
            }, 200);
          });
        }

        if (this.tagSelect) {
          this.tagSelect.addEventListener('change', (event) => {
            this.filters.tag = event.target.value;
            this.currentPage = 1;
            this.applyFilters();
          });
        }

        if (this.productSelect) {
          this.productSelect.addEventListener('change', (event) => {
            this.filters.product = event.target.value;
            this.currentPage = 1;
            this.applyFilters();
          });
        }
      }

      applyFilters() {
        this.filtered = this.allArticles.filter((article) => {
          if (this.filters.search) {
            const haystack = `${article.title} ${article.excerpt}`.toLowerCase();
            if (!haystack.includes(this.filters.search)) return false;
          }
          if (this.filters.tag) {
            if (!(article.tags || []).includes(this.filters.tag)) return false;
          }
          if (this.filters.product) {
            if (!(article.products || []).includes(this.filters.product)) return false;
          }
          return true;
        });
        this.renderCurrentPage();
      }

      renderCurrentPage(options = {}) {
        const start = (this.currentPage - 1) * this.perPage;
        const pageArticles = this.filtered.slice(start, start + this.perPage);

        if (!options.skipGrid && this.gridEl) {
          this.gridEl.innerHTML = '';
          pageArticles.forEach((article) => this.gridEl.appendChild(this.renderCard(article)));
        }

        if (this.countEl) {
          const count = this.filtered.length;
          this.countEl.textContent = `Showing ${count} ${count === 1 ? 'post' : 'posts'}`;
        }

        this.renderPagination();

        // Only touch empty-state / grid visibility once we've actually
        // redrawn the grid from real (search/filter/fetch) data — never
        // on the initial server-rendered paint.
        if (!options.skipGrid) {
          const isEmpty = this.filtered.length === 0;
          if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
          if (this.gridEl) this.gridEl.style.display = isEmpty ? 'none' : 'grid';
        }
      }

      renderCard(article) {
        const el = document.createElement('div');
        el.className = 'blog-archive__card';

        const imageHtml = article.image
          ? `<div class="blog-archive__card-image-wrapper"><img src="${article.image}" alt="${this.escapeHtml(
              article.title
            )}" loading="lazy" class="blog-archive__card-image"></div>`
          : '';
        const titleHtml = this.showTitle
          ? `<h3 class="blog-archive__card-title">${this.escapeHtml(article.title)}</h3>`
          : '';

        const metaParts = [];
        if (this.showAuthor && article.author) metaParts.push(this.escapeHtml(article.author));
        if (this.showDate && article.date_display) metaParts.push(this.escapeHtml(article.date_display));
        const metaHtml = metaParts.length
          ? `<div class="blog-archive__card-meta">${metaParts.map((part) => `<span>${part}</span>`).join('')}</div>`
          : '';

        const tagsHtml =
          this.showTags && article.tags && article.tags.length
            ? `<div class="blog-archive__card-tags">${article.tags
                .map(
                  (tag) =>
                    `<span class="blog-archive__card-tag color-${this.accentScheme}">${this.escapeHtml(tag)}</span>`
                )
                .join('')}</div>`
            : '';

        el.innerHTML = `
          <a href="${article.url}" class="blog-archive__card-link">
            ${imageHtml}
            <div class="blog-archive__card-content">
              ${titleHtml}
              ${article.excerpt ? `<p class="blog-archive__card-excerpt">${this.escapeHtml(article.excerpt)}</p>` : ''}
              ${metaHtml}
              ${tagsHtml}
            </div>
          </a>
        `;
        return el;
      }

      renderPagination() {
        if (!this.paginationEl) return;
        const totalPages = Math.ceil(this.filtered.length / this.perPage) || 1;
        this.paginationEl.innerHTML = '';
        if (totalPages <= 1) return;

        const makeButton = (label, page, opts = {}) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'blog-archive__page-button' + (opts.active ? ' is-active' : '');
          btn.textContent = label;
          if (opts.disabled) btn.disabled = true;
          btn.addEventListener('click', () => {
            this.currentPage = page;
            this.renderCurrentPage();
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
          return btn;
        };

        this.paginationEl.appendChild(
          makeButton('Previous', this.currentPage - 1, { disabled: this.currentPage === 1 })
        );
        for (let i = 1; i <= totalPages; i++) {
          this.paginationEl.appendChild(makeButton(String(i), i, { active: i === this.currentPage }));
        }
        this.paginationEl.appendChild(
          makeButton('Next', this.currentPage + 1, { disabled: this.currentPage === totalPages })
        );
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text == null ? '' : String(text);
        return div.innerHTML;
      }
    }
  );
}
